import { Component, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { OrganizationContactModel, CreateOrganizationContactRequest } from '../../../../core/models/data/organization-contact.model';
import { OrganizationContactType } from '../../../../core/enums/data.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { PhonePipe, CountryCodePipe, PhoneExtensionPipe } from '../../../../core/pipes/phone-number/phone-number.pipe';
import { OrganizationContactFormModalComponent } from '../organization-contact-form-modal/organization-contact-form-modal.component';
import { CommonChecks } from '../../../../core/utilities/common-checks.utilities';

@Component({
  selector: 'ms-organization-contacts',
  standalone: true,
  imports: [
    FaDuotoneIconComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    OrganizationContactFormModalComponent,
    PhonePipe,
    CountryCodePipe,
    PhoneExtensionPipe
  ],
  templateUrl: './organization-contacts.component.html',
  styleUrl: './organization-contacts.component.scss'
})
export class OrganizationContactsComponent {

  private organizationService = inject(OrganizationService);
  private bootstrapService = inject(BootstrapService);

  public organization = input<OrganizationModel | undefined>(undefined);
  public organizationUpdated = output<OrganizationModel>();

  @ViewChild('removeContactModal') removeContactModalRef!: ConfirmationModalComponent;
  @ViewChild('contactInfoModal') contactInfoModalRef!: InfoModalComponent;
  @ViewChild('contactFormModal') contactFormModalRef!: OrganizationContactFormModalComponent;

  public OrganizationsMessagingConstants = OrganizationsMessagingConstants;

  public contacts = computed<OrganizationContactModel[]>(() => this.organization()?.contacts ?? []);
  public hasContacts = computed<boolean>(() => this.contacts().length > 0);
  public isSubmitting = signal<boolean>(false);

  private contactPendingRemoval = signal<OrganizationContactModel | undefined>(undefined);

  public hasPhoneNumber(contact: OrganizationContactModel): boolean {
    return !CommonChecks.isNullOrUndefinedOrEmpty(contact.phoneNumber);
  }

  public hasEmail(contact: OrganizationContactModel): boolean {
    return !CommonChecks.isNullOrUndefinedOrEmpty(contact.emailAddress);
  }

  public hasPhoneExtension(contact: OrganizationContactModel): boolean {
    return !CommonChecks.isNullOrUndefinedOrEmpty(contact.phoneExtension);
  }

  public openContactForm(): void {
    this.contactFormModalRef.openModal();
  }

  public openRemoveConfirmation(contact: OrganizationContactModel): void {
    this.contactPendingRemoval.set(contact);
    const message = OrganizationsMessagingConstants.ORGANIZATIONS_REMOVE_CONTACT_CONFIRMATION()
      .replace('{contactName}', contact.name ?? '');
    this.removeContactModalRef.modalType.set(ModalTypes.Question);
    this.removeContactModalRef.message.set(message);
    this.bootstrapService.manageModal('#removeContactModal', ModalActions.Show);
  }

  public async confirmRemoveContact(): Promise<void> {
    const target = this.contactPendingRemoval();
    const organizationId = this.organization()?.organizationId;
    if (!target || !organizationId) {
      this.bootstrapService.manageModal('#removeContactModal', ModalActions.Hide);
      return;
    }

    this.isSubmitting.set(true);
    try {
      const remainingContacts: CreateOrganizationContactRequest[] = this.contacts()
        .filter(c => c.organizationContactId !== target.organizationContactId)
        .map(c => this.toRequest(c));

      const result = await this.organizationService.updateOrganizationContacts(organizationId, { contacts: remainingContacts });

      this.bootstrapService.manageModal('#removeContactModal', ModalActions.Hide);

      if (result.success && result.data) {
        this.organizationUpdated.emit(result.data);
        this.contactInfoModalRef.modalType.set(ModalTypes.Success);
        this.contactInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_REMOVE_SUCCESS());
      } else {
        this.contactInfoModalRef.modalType.set(ModalTypes.Failure);
        this.contactInfoModalRef.message.set(result.message ?? OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_REMOVE_FAILURE());
      }
      this.bootstrapService.manageModal('#contactInfoModal', ModalActions.Show);
    } finally {
      this.isSubmitting.set(false);
      this.contactPendingRemoval.set(undefined);
    }
  }

  public cancelRemoveContact(): void {
    this.bootstrapService.manageModal('#removeContactModal', ModalActions.Hide);
    this.contactPendingRemoval.set(undefined);
  }

  public closeInfoModal(): void {
    this.bootstrapService.manageModal('#contactInfoModal', ModalActions.Hide);
  }

  public onContactSaved(updatedOrganization: OrganizationModel): void {
    this.organizationUpdated.emit(updatedOrganization);
    this.contactInfoModalRef.modalType.set(ModalTypes.Success);
    this.contactInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_SAVE_SUCCESS());
    this.bootstrapService.manageModal('#contactInfoModal', ModalActions.Show);
  }

  public onContactSaveFailed(message: string | undefined): void {
    this.contactInfoModalRef.modalType.set(ModalTypes.Failure);
    this.contactInfoModalRef.message.set(message ?? OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_SAVE_FAILURE());
    this.bootstrapService.manageModal('#contactInfoModal', ModalActions.Show);
  }

  public typeLabel(type: OrganizationContactType): string {
    return type === OrganizationContactType.Billing ? 'Billing' : 'General';
  }

  public contactTypeIcon(type: OrganizationContactType): string {
    return type === OrganizationContactType.Billing ? 'file-invoice-dollar' : 'circle-user';
  }

  private toRequest(contact: OrganizationContactModel): CreateOrganizationContactRequest {
    return {
      type: contact.type,
      name: contact.name ?? '',
      emailAddress: contact.emailAddress,
      phoneCountryCode: contact.phoneCountryCode,
      phoneNumber: contact.phoneNumber,
      phoneExtension: contact.phoneExtension
    };
  }
}
